import { Inject, Injectable } from '@angular/core';
import { DistributionGroup } from '@bidi/common-services';
import ChatClient, { Config, JID } from '@bidi/chat-client';
import { createFeatureSelector, select, Store } from '@ngrx/store';
import differenceBy from 'lodash/fp/differenceBy';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, pairwise, shareReplay, startWith, withLatestFrom } from 'rxjs/operators';
import { Channel, ChannelGroup } from '../../models';
import { ifObservable } from '../../ngrx-helpers';
import { State } from './ngrx/reducers';
import {
  ChatLogin,
  ChatLogout,
  CreateChannelsFromDistributionGroups,
  IncomingMessage,
  OutgoingMessage,
  SelectChannel,
  SelectGroup,
  StatusChange
} from './ngrx/actions';

@Injectable()
export class ChatService {
  static featureName = '@bidi/common-components Chat';

  chatClient: ChatClient;

  constructor (
    @Inject('ENV') private environment,
    private store: Store<any>
  ) {
  }

  private subscription: Subscription;

  private state$: Observable<State> = this.store.pipe(select(createFeatureSelector(ChatService.featureName)));

  userJid$: Observable<JID> = this.state$.pipe(select('userJid'));

  loggedIn$: Observable<boolean> = this.state$.pipe(
    select('status'),
    map(s => s === 'online')
  );

  channels$: Observable<Channel[]> = ifObservable(
    this.loggedIn$,
    this.state$.pipe(select('channels')),
    of([])
  );

  selectedGroup$: Observable<ChannelGroup> = combineLatest(
    this.channels$,
    this.state$.pipe(select('selectedGroup'))
  ).pipe(
    map(([channels, groupId]) => channels.filter(chan => chan.groupId === groupId)),
    shareReplay(1)
  );

  selectedChannel$: Observable<Channel> = combineLatest(
    this.channels$,
    this.state$.pipe(select('selectedChannel'))
  ).pipe(
    map(([channels, jid]) => channels.find(chan => chan.jid === jid)),
    withLatestFrom(this.userJid$),
    map(([channel, jid]) => channel && {
      ...channel,
      history: channel.history.map(msg => ({
        ...msg,
        mine: msg.from === jid.bare().toString()
      }))
    }),
    shareReplay(1)
  );

  initClient (config: Config): this {
    this.destroyClient();

    this.chatClient = new ChatClient(config)
      .onMessage(msg => this.store.dispatch(new IncomingMessage(msg)))
      .onStatusChange(status => this.store.dispatch(new StatusChange(status)));

    return this;
  }

  login (username: string, distributionGroups$: Observable<DistributionGroup[]>) {
    const { host, endpoint } = this.environment.chat;

    this.store.dispatch(new ChatLogin({ service: endpoint, username, password: '1234' }));

    this.subscription = distributionGroups$.pipe(
      startWith([]),
      pairwise(),
      map(([prev, curr]) => differenceBy('_id', curr, prev)),
    ).subscribe(groups => {
      this.store.dispatch(new CreateChannelsFromDistributionGroups({ groups, host }));
    });
  }

  logout () {
    this.store.dispatch(new ChatLogout());

    this.destroyClient();

    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  selectGroup (group: ChannelGroup) {
    this.store.dispatch(new SelectGroup(group));
  }

  selectChannel (channel: Channel) {
    this.store.dispatch(new SelectChannel(channel));
  }

  send (message, to: Channel) {
    this.store.dispatch(new OutgoingMessage({ message, to }));
  }

  private destroyClient () {
    if (this.chatClient) {
      this.chatClient.destroy();
      this.chatClient = null;
    }
  }
}
